import { languageDisplayLabel } from '@/api/enums';
import { CampaignTemplate } from '@/client/campaigns';
import { StarRating } from '@/ui/star-rating';
import { FlexContainer } from '@/ui/styled-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UsersScore } from './UsersScore';

const StyledInfo = styled(FlexContainer)`
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: var(--small-padding) var(--default-padding);
  color: var(--black-main);
  flex: 1 0 40%;
  height: 100%;
  z-index: 1;

  .red-text {
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    font-weight: 500;
    color: var(--red-main);
    margin-bottom: var(--medium-padding);
  }

  &.preview {
    padding: var(--default-padding) calc(2 * var(--default-padding));
    border-right: 1px solid var(--gray-dark);
    box-shadow: 5px 0 5px -5px var(--gray-dark) !important;
    overflow-y: scroll;
  }

  &.catalogue {
    flex: 1 1 50%;
  }

  h1 {
    font-size: var(--heading-medium-font-size);
    line-height: var(--heading-medium-line-height);
    font-weight: 700;
  }

  ul {
    margin: 0;
    padding-inline: var(--default-padding);
  }

  h3 {
    margin-top: var(--medium-padding);
    margin-bottom: 0;
  }

  h3,
  div {
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
  }

  .languages {
    margin-block: var(--medium-padding);
    color: var(--gray-darker);
    font-weight: 500;
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
  }

  .ql-editor {
    height: auto;
    overflow-y: unset;
    width: 100%;
    margin: 0;
    padding: 0;
    background: transparent;

    pre,
    blockquote,
    .ql-code-block-container {
      background: transparent;
    }
  }
`;

export type CampaignInfoPropsType = {
  campaign: CampaignTemplate;
  className?: string;
  hideHeader?: boolean;
};

export const CampaignInfo: React.FC<CampaignInfoPropsType> = ({
  campaign,
  className,
  hideHeader = false,
}) => {
  const { t } = useTranslation();
  const mt = campaign?.masterTemplate ?? null;
  const mtTranslation =
    mt?.translations?.find((tr) => tr.language === campaign?.language) ??
    mt?.translations?.find((tr) => tr.language === 'en_GB') ??
    null;
  const displayName = mtTranslation?.name ?? campaign.name;
  const displayDescription =
    mtTranslation?.description ?? campaign?.description ?? '';
  const displayDifficultyValue = mt?.difficulty ?? campaign?.difficulty;
  const displayPhishingSign =
    mt?.phishingSign?.key ?? campaign?.meta?.phishingSign;

  const phishingSignDescription = mt?.phishingSign?.translations
    ? mt.phishingSign.translations.find(
        (tr) => tr.language === (campaign?.language as string),
      )?.content ??
      mt.phishingSign.translations.find((tr) => tr.language === 'en_GB')
        ?.content ??
      null
    : null;

  const characteristics = mt?.characteristics
    ?.map((c) => {
      const tr =
        c.translations?.find(
          (tr) => tr.language === (campaign?.language as string),
        ) ?? c.translations?.find((tr) => tr.language === 'en_GB');
      return tr?.description ?? null;
    })
    .filter((d): d is string => !!d);

  return (
    <StyledInfo className={className}>
      {!hideHeader && displayPhishingSign && (
        <div className="red-text">
          {t(`campaign.template.${displayPhishingSign}`)}
        </div>
      )}
      {!hideHeader && <h1 className="m-0">{displayName}</h1>}
      {campaign?.language && (
        <div className="languages">
          {languageDisplayLabel[campaign.language]}
        </div>
      )}
      <div
        className="ql-editor"
        dangerouslySetInnerHTML={{
          __html: displayDescription,
        }}
      />
      {!!mt && !!displayPhishingSign && (
        <h3>
          {`${t('primaryLearningObjective')}: `}
          {t(`campaign.template.${displayPhishingSign}`)}
        </h3>
      )}
      {!!phishingSignDescription && (
        <div
          className="ql-editor"
          dangerouslySetInnerHTML={{ __html: phishingSignDescription }}
        />
      )}
      {!!characteristics?.length && (
        <>
          <h3>{t('phishingCharacteristics')}</h3>
          <ul>
            {characteristics.map((desc, i) => (
              <li key={i}>{desc}</li>
            ))}
          </ul>
        </>
      )}
      {!!displayDifficultyValue && (
        <h3
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--xsmall-padding)',
          }}
        >
          {`${t('generic.difficulty')}: `}
          <StarRating
            value={displayDifficultyValue}
            maxStars={5}
            icon="fish"
            disabled
          />
        </h3>
      )}

      {!!campaign?.benchmarkSummary && (
        <>
          <h3>{t('campaign.template.usersScore')}</h3>
          <UsersScore benchmark={campaign.benchmarkSummary} />
        </>
      )}
    </StyledInfo>
  );
};
